import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { devtools } from 'zustand/middleware';

/**
 * Grid row-selection state, shared by the resource grids and the M3c bulk actions.
 * Mirrors the Angular contract (BrowseController: `scope.resources` = selected ids;
 * `selectAll` = "all matching") for ag-Grid's infinite row model where rows unload:
 *
 * - `mode: 'none'` — nothing selected.
 * - `mode: 'some'` — the explicit `selected` id set (feeds a bulk action's id list).
 * - `mode: 'all'`  — every matching row across all pages (feeds a bulk action's
 *   filter/query instead of an id list).
 *
 * There is deliberately NO per-row exclusion in `all` mode: the filter-based bulk
 * endpoints (tag/batch/report by filter) can't express an exclude list, and the
 * unloaded blocks mean we can't enumerate the matching ids client-side. So, matching
 * Angular's grid (which force-re-selects a row clicked while select-all is on),
 * individual checkboxes are locked while in `all` mode — toggleRow is a no-op there.
 *
 * Uses a plain id map rather than Set so immer needs no enableMapSet().
 */
type SelectionMode = 'none' | 'some' | 'all';

interface ResourceSelectionState {
  mode: SelectionMode;
  /** Individually checked ids (mode === 'some'). */
  selected: Record<string, true>;

  /** Toggle one row's checkbox. No-op in `all` mode (rows are locked selected). */
  toggleRow: (id: string) => void;
  /** Enter select-all-matching mode (clears any prior explicit ids). */
  selectAllMatching: () => void;
  /** Clear all selection back to 'none'. */
  clear: () => void;

  /** Is this row currently selected? */
  isSelected: (id: string) => boolean;
  /** Number of selected rows, given the grid's total matching row count. */
  count: (totalRows: number) => number;
}

export const useResourceSelectionStore = create<ResourceSelectionState>()(
  devtools(
    immer((set, get) => ({
      mode: 'none',
      selected: {},

      toggleRow: (id) =>
        set((state) => {
          // In select-all, rows can't be individually deselected (see file docstring).
          if (state.mode === 'all') return;
          if (state.selected[id]) {
            delete state.selected[id];
            if (Object.keys(state.selected).length === 0) state.mode = 'none';
          } else {
            state.selected[id] = true;
            state.mode = 'some';
          }
        }),

      selectAllMatching: () =>
        set((state) => {
          state.mode = 'all';
          state.selected = {};
        }),

      clear: () =>
        set((state) => {
          state.mode = 'none';
          state.selected = {};
        }),

      isSelected: (id) => {
        const s = get();
        return s.mode === 'all' ? true : !!s.selected[id];
      },

      count: (totalRows) => {
        const s = get();
        return s.mode === 'all' ? totalRows : Object.keys(s.selected).length;
      },
    })),
    { name: 'ResourceSelectionStore' }
  )
);
